import React from 'react';
import { ChevronRight, RefreshCw, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Shop } from '@/lib/supabase/shops/types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CollapsedShopFilterProps {
  setIsCollapsed: (collapsed: boolean) => void;
  shops: Shop[];
  isLoading: boolean;
  toggleShopSelection: (id: string) => void;
  isSingleSelectionMode: boolean;
  singleSelectedShop: string;
  selectedShops: string[];
  refreshShops?: () => Promise<void>;
}

const CollapsedShopFilter: React.FC<CollapsedShopFilterProps> = ({
  setIsCollapsed,
  shops,
  isLoading,
  toggleShopSelection,
  isSingleSelectionMode,
  singleSelectedShop,
  selectedShops,
  refreshShops
}) => {
  return (
    <div className="collapsed-shops-sidebar h-full w-16 flex flex-col items-center border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <div className="w-full py-4 px-2 flex justify-center border-b border-gray-200 dark:border-gray-800">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setIsCollapsed(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                aria-label="Expand sidebar"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Expand shops sidebar</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {refreshShops && (
        <div className="mt-2 mb-2 w-full flex justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={refreshShops}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2"
                  aria-label="Refresh shops"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Refresh shops list</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      <div className="w-full flex-1 overflow-y-auto py-2 px-1">
        {shops.map((shop) => (
          <TooltipProvider key={shop.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => 
                    isSingleSelectionMode 
                      ? toggleShopSelection(shop.id) 
                      : toggleShopSelection(shop.id)
                  }
                  className={cn(
                    "w-full rounded-md p-2 mb-1 flex justify-center items-center",
                    isSingleSelectionMode
                      ? singleSelectedShop === shop.id
                        ? "bg-custom-primary/10 text-custom-primary"
                        : "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                      : selectedShops.includes(shop.id)
                        ? "bg-custom-primary/10 text-custom-primary"
                        : "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  )}
                >
                  <Store className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{shop.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>
    </div>
  );
};

export default CollapsedShopFilter;
